import React, { useRef, useImperativeHandle, forwardRef } from 'react';
import EmailEditor from 'react-email-editor';
import './UnlayerDocumentEditor.css';

const UnlayerDocumentEditor = forwardRef(({
  initialContent = null,
  onContentChange,
  onLoad,
  onReady,
  height = 400,
  minHeight = 300,
  appearance = 'light',
  projectId = null,
  locale = 'en-US',
  tools = null,
  options = {},
  className = '',
  style = {},
  loading = false,
  error = null,
  placeholder = 'Start creating your document...',
  mergeTags = null, // Allow custom merge tags
  disabled = false,
  showLoadingSpinner = true,
}, ref) => {
  const emailEditorRef = useRef(null);

  // Default editor options with responsive design
  const defaultOptions = {
    displayMode: 'email',
    locale,
    appearance: {
      theme: appearance,
      panels: {
        tools: {
          dock: window.innerWidth < 768 ? 'top' : 'left'
        }
      }
    },
    features: {
      preview: true,
      imageEditor: true,
      stockImages: false,
      undoRedo: true,
      textEditor: {
        spellChecker: true,
        cleanPaste: true
      }
    },
    fonts: {
      showDefaultFonts: true,
      customFonts: [
        {
          label: 'Arial',
          value: 'Arial,sans-serif'
        },
        {
          label: 'Times New Roman', 
          value: 'Times New Roman,serif'
        },
        {
          label: 'Helvetica',
          value: 'Helvetica,sans-serif'
        },
        {
          label: 'Georgia',
          value: 'Georgia,serif'
        },
        {
          label: 'Verdana',
          value: 'Verdana,sans-serif'
        }
      ]
    },
    editor: {
      minRows: 3,
      maxRows: 20
    },
    mergeTags: mergeTags || [
      {
        name: 'Candidate Name',
        value: '{{candidate_name}}',
        sample: 'John Doe'
      },
      {
        name: 'First Name',
        value: '{{first_name}}',
        sample: 'John'
      },
      {
        name: 'Last Name',
        value: '{{last_name}}',
        sample: 'Doe'
      },
      {
        name: 'Email',
        value: '{{email}}',
        sample: 'john.doe@example.com'
      },
      {
        name: 'Phone',
        value: '{{phone}}',
        sample: '+1 234 567 8900'
      },
      {
        name: 'Designation',
        value: '{{designation}}',
        sample: 'Software Engineer'
      },
      {
        name: 'Department',
        value: '{{department}}',
        sample: 'Engineering'
      },
      {
        name: 'Company Name',
        value: '{{company_name}}',
        sample: 'HR Globes'
      },
      {
        name: 'Joining Date',
        value: '{{joining_date}}',
        sample: 'January 1, 2024'
      },
      {
        name: 'CTC',
        value: '{{ctc}}',
        sample: '$75,000'
      },
      {
        name: 'HR Name',
        value: '{{hr_name}}',
        sample: 'Sarah Johnson'
      },
      {
        name: 'Interview Date',
        value: '{{interview_date}}',
        sample: 'December 15, 2023'
      },
      {
        name: 'Offer Date',
        value: '{{offer_date}}',
        sample: 'December 20, 2023'
      }
    ],
    ...options
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    // Export HTML content
    exportHtml: () => {
      return new Promise((resolve) => {
        emailEditorRef.current?.editor?.exportHtml((data) => {
          resolve(data);
        });
      });
    },
    
    // Export design JSON
    exportDesign: () => {
      return new Promise((resolve) => {
        emailEditorRef.current?.editor?.saveDesign((design) => {
          resolve(design);
        });
      });
    },
    
    // Load design from JSON
    loadDesign: (design) => {
      if (emailEditorRef.current?.editor && design) {
        emailEditorRef.current.editor.loadDesign(design);
      }
    },
    
    // Load blank design
    loadBlank: () => {
      if (emailEditorRef.current?.editor) {
        emailEditorRef.current.editor.loadBlank();
      }
    },
    
    // Get editor instance
    getEditor: () => {
      return emailEditorRef.current?.editor;
    }
  }));

  const handleEditorLoad = () => {
    try {
      // Call custom onLoad callback if provided
      if (onLoad) {
        onLoad(emailEditorRef.current?.editor);
      }
      
      // Load initial content if provided
      if (initialContent && !disabled) {
        setTimeout(() => {
          try {
            // If initialContent is a design object, load it
            if (typeof initialContent === 'object' && initialContent.body) {
              emailEditorRef.current?.editor?.loadDesign(initialContent);
            } 
            // If it's HTML, convert to basic design
            else if (typeof initialContent === 'string' && initialContent.trim()) {
              const basicDesign = {
                body: {
                  rows: [{
                    cells: [1],
                    columns: [{
                      contents: [{
                        type: 'text',
                        values: {
                          text: initialContent
                        }
                      }]
                    }]
                  }]
                }
              };
              emailEditorRef.current?.editor?.loadDesign(basicDesign);
            } else {
              // Load blank design
              emailEditorRef.current?.editor?.loadBlank();
            }
          } catch (error) {
            console.warn('Error loading initial content:', error);
            emailEditorRef.current?.editor?.loadBlank();
          }
        }, 100);
      } else {
        // Load blank design by default
        setTimeout(() => {
          emailEditorRef.current?.editor?.loadBlank();
        }, 100);
      }
      
      // Call onReady callback if provided
      if (onReady) {
        setTimeout(() => onReady(emailEditorRef.current?.editor), 200);
      }
    } catch (error) {
      console.error('Error in editor load:', error);
    }
  };

  const onDesignLoad = () => {
    // Trigger content change when design loads
    if (onContentChange) {
      emailEditorRef.current?.editor?.exportHtml((data) => {
        onContentChange(data);
      });
    }
  };

  const handleDesignChange = () => {
    // Trigger content change callback
    if (onContentChange) {
      emailEditorRef.current?.editor?.exportHtml((data) => {
        onContentChange(data);
      });
    }
  };

  // Show loading state
  if (loading && showLoadingSpinner) {
    return (
      <div 
        className={`unlayer-document-editor loading ${className}`} 
        style={{ height: `${height}px`, minHeight: `${minHeight}px`, ...style }}
      >
        <div className="d-flex align-items-center justify-content-center h-100">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading editor...</span>
            </div>
            <p className="mt-2 text-muted small">Loading editor...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div 
        className={`unlayer-document-editor error ${className}`} 
        style={{ height: `${height}px`, minHeight: `${minHeight}px`, ...style }}
      >
        <div className="d-flex align-items-center justify-content-center h-100">
          <div className="text-center">
            <i className="fa fa-exclamation-triangle text-danger mb-2" style={{ fontSize: '2rem' }}></i>
            <p className="text-danger mb-2">{error}</p>
            <button 
              className="btn btn-outline-primary btn-sm"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`unlayer-document-editor ${disabled ? 'disabled' : ''} ${className}`}
      style={{ height: `${height}px`, minHeight: `${minHeight}px`, ...style }}
    >
      <EmailEditor
        ref={emailEditorRef}
        onLoad={handleEditorLoad}
        onDesignLoad={onDesignLoad}
        onChange={handleDesignChange}
        options={defaultOptions}
        projectId={projectId}
        style={{ height: `${minHeight}px`, width: '100%' }}
      />
      {disabled && (
        <div className="editor-overlay d-flex align-items-center justify-content-center">
          <div className="bg-light p-3 rounded text-center">
            <i className="fa fa-lock text-muted mb-2"></i>
            <p className="text-muted mb-0 small">Editor is disabled</p>
          </div>
        </div>
      )}
    </div>
  );
});

UnlayerDocumentEditor.displayName = 'UnlayerDocumentEditor';

export default UnlayerDocumentEditor;